import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
// Landing + Guest render on the critical path (marketing hero and the QR-scan
// destination). Everything else is dashboard / auth flow and can lazy load.
import { LandingPage } from './pages/LandingPage';
import { GuestPage } from './pages/GuestPage';
import { startUploadWorker } from './lib/uploadWorker';

const CoupleLoginPage = lazy(() => import('./pages/CoupleLoginPage').then((m) => ({ default: m.CoupleLoginPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const CreateEventPage = lazy(() => import('./pages/CreateEventPage').then((m) => ({ default: m.CreateEventPage })));
const AccountPage = lazy(() => import('./pages/AccountPage').then((m) => ({ default: m.AccountPage })));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })));
const PrivacyPage = lazy(() => import('./pages/LegalPage').then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/LegalPage').then((m) => ({ default: m.TermsPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })));
const AlbumPage = lazy(() => import('./pages/AlbumPage').then((m) => ({ default: m.AlbumPage })));

// Sentry: prod error tracking. No-op if DSN is unset.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (sentryDsn) {
  void import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: sentryDsn,
      tracesSampleRate: 0.0,
      environment: import.meta.env.MODE,
    });
  });
}

// Kick the background upload worker on app load. It picks up any queued items
// left behind by a previous session (page close mid-upload).
startUploadWorker();

// PWA update behaviour. `registerSW` from vite-plugin-pwa returns an update
// function; when combined with `skipWaiting + clientsClaim` in vite.config,
// this guarantees that home-screen installs pick up new deploys on their
// next launch instead of getting stuck on a cached shell.
//
// We reload once when the new SW takes control so the running tab sees the
// fresh HTML/JS. Guarded with sessionStorage so we don't loop.
void import('virtual:pwa-register').then(({ registerSW }) => {
  registerSW({
    immediate: true,
    onNeedRefresh() {
      // A new version is waiting. With skipWaiting on, it activates itself;
      // we just need to reload to pick up the new assets.
      if (sessionStorage.getItem('kept:sw-reloaded') === '1') return;
      sessionStorage.setItem('kept:sw-reloaded', '1');
      window.location.reload();
    },
    onOfflineReady() {
      // First install — nothing to do. The service worker is ready.
    },
  });
}).catch(() => {
  // vite-plugin-pwa virtual module isn't available in dev; ignore.
});

// Fallback shown while a lazy route bundle is downloading. Minimal so it
// doesn't cause layout shift.
function RouteFallback() {
  return <div className="min-h-full canvas-warm" />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/event/:slug" element={<GuestPage />} />
          <Route path="/album/:slug" element={<AlbumPage />} />
          <Route path="/host/login" element={<CoupleLoginPage />} />
          <Route path="/host/create" element={<CreateEventPage />} />
          {/* Legacy /couple/* — keep working for anyone who bookmarked / shared. */}
          <Route path="/couple/login" element={<Navigate to="/host/login" replace />} />
          <Route path="/couple/create" element={<Navigate to="/host/create" replace />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/dashboard/:eventId" element={<DashboardPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </React.StrictMode>
);
